// apps/admin/src/routes/Invite.tsx
import { useMemo, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { MIN_PASSWORD_LENGTH, validatePasswordComplexity } from "@jourfx/convex/lib/password";
import { Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { resolveInternalLoginUrl } from "../lib/internalAppUrl";

function getConvexBaseUrl() {
  return (
    import.meta.env.VITE_CONVEX_SITE_URL ||
    import.meta.env.VITE_CONVEX_URL ||
    "http://localhost:3211"
  );
}

function getBrandLogoUrl(updatedAt?: number) {
  const base = getConvexBaseUrl().replace(/\/$/, "");
  const suffix = typeof updatedAt === "number" ? `?v=${updatedAt}` : "";
  return `${base}/api/branding/logo-primary${suffix}`;
}

function sanitizeInviteError(rawMessage: string) {
  return rawMessage
    .replace(/^\[CONVEX[^\]]*\]\s*/i, "")
    .replace(/^\[Request ID:[^\]]*\]\s*/i, "")
    .replace(/^Server Error Uncaught Error:\s*/i, "")
    .replace(/\s+at handler\s+\([^)]*\)\s*Called by client\s*$/i, "")
    .replace(/\s*Called by client\s*$/i, "")
    .trim();
}

export function Invite() {
  const { token } = useParams({ from: "/invite/$token" });
  const { t } = useTranslation("invite");

  const invite = useQuery(api.auth.invite.validateInvite, { token });
  const branding = useQuery((api as any).admin.branding.getPublic, {});
  const acceptInvite = useMutation(api.auth.invite.acceptInvite);
  const loginUrl = useMemo(() => resolveInternalLoginUrl(), []);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Loading state
  if (invite === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Success must win over the live invite query flipping to "used" after acceptance.
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-success mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">
            {t("success.title")}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t("success.description")}
          </p>
          <a
            href={loginUrl}
            className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            {t("success.toLogin")}
          </a>
        </div>
      </div>
    );
  }

  // Invalid invite
  if (!invite.valid) {
    const reason = invite.reason as string;
    const message =
      reason === "not_found"
        ? t("invalid.not_found")
        : reason === "used"
          ? t("invalid.used")
          : reason === "expired"
            ? t("invalid.expired")
            : t("invalid.fallback");

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-error mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">
            {t("invalid.title")}
          </h1>
          <p className="text-muted-foreground mb-6">
            {message}
          </p>
          <a
            href={loginUrl}
            className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            {t("invalid.toLogin")}
          </a>
        </div>
      </div>
    );
  }

  const roleLabel =
    invite.role === "administrator"
      ? t("roles.administrator")
      : invite.role === "gutachter"
        ? t("roles.gutachter")
        : invite.role === "assistent"
          ? t("roles.assistent")
          : invite.role === "versicherter"
            ? t("roles.versicherter")
            : invite.role;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t("errors.minLength"));
      return;
    }

    if (!validatePasswordComplexity(password).valid) {
      setError(t("errors.requirements"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("errors.mismatch"));
      return;
    }

    setIsSubmitting(true);

    try {
      await acceptInvite({ token, password });
      setSuccess(true);
    } catch (err) {
      if (err instanceof Error) {
        const cleanMessage = sanitizeInviteError(err.message);
        setError(cleanMessage || t("errors.unknown"));
        return;
      }
      setError(t("errors.unknown"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <img
              src={getBrandLogoUrl(branding?.updatedAt)}
              alt={invite.orgName}
              className="mx-auto mb-4 h-12 w-auto object-contain"
            />
            <h2 className="text-xl font-semibold text-foreground">
              {t("welcome", { orgName: invite.orgName })}
            </h2>
            <p className="text-muted-foreground mt-2">
              {t("joiningAs", { role: roleLabel })}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (read-only) */}
            <div>
              <label
                htmlFor="invite-email"
                className="block text-sm font-medium text-foreground mb-1"
              >
                {t("form.email")}
              </label>
              <input
                id="invite-email"
                type="email"
                value={invite.email}
                disabled
                autoComplete="email"
                className="w-full px-3 py-2 border border-input rounded-lg bg-muted text-muted-foreground"
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="invite-password"
                className="block text-sm font-medium text-foreground mb-1"
              >
                {t("form.password")}
              </label>
              <input
                id="invite-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("form.passwordPlaceholder")}
                required
                minLength={MIN_PASSWORD_LENGTH}
                autoComplete="new-password"
                className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
              />
              <p className="mt-2 text-sm text-muted-foreground">
                {t("form.passwordRequirements")}
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="invite-confirm-password"
                className="block text-sm font-medium text-foreground mb-1"
              >
                {t("form.confirmPassword")}
              </label>
              <input
                id="invite-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("form.confirmPasswordPlaceholder")}
                required
                autoComplete="new-password"
                className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-error-muted border border-error-border rounded-lg text-error-muted-foreground text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t("form.submitting")}
                </>
              ) : (
                t("form.submit")
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-4">
          {t("alreadyHaveAccount")}{" "}
          <a href={loginUrl} className="text-primary hover:underline">
            {t("login")}
          </a>
        </p>
      </div>
    </div>
  );
}
